import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a metafield-based fake variant picker.
 * When a variant is selected, it updates the product image, title, and price.
 */
class ProductMetafieldVariantPicker extends HTMLElement {
  /** @type {boolean} */
  #isUpdating = false;
  /** @type {boolean} */
  #isRestoring = false;

  connectedCallback() {
    this.addEventListener('change', this.handleVariantChange.bind(this));

    const currentProductId = this.dataset.productId;
    const allRadios = this.querySelectorAll('input[type="radio"]');

    const targetRadio = this.querySelector(`input[data-variant-product-id="${currentProductId}"]`);
    if (targetRadio instanceof HTMLInputElement) {
      allRadios.forEach((radio) => {
        if (radio instanceof HTMLInputElement) {
          radio.checked = false;
        }
      });
      targetRadio.checked = true;

      if (targetRadio.dataset.variantHasQuantity === 'true' && !this.#isUpdating && !this.#isRestoring) {
        this.updateProductData(targetRadio);
      }
      return;
    }

    const alreadyChecked = this.querySelector('input[type="radio"]:checked');
    if (alreadyChecked instanceof HTMLInputElement) {
      if (alreadyChecked.dataset.variantHasQuantity === 'true' && !this.#isUpdating && !this.#isRestoring) {
        this.updateProductData(alreadyChecked);
      }
      return;
    }

    const firstAvailable = this.querySelector('input[type="radio"][data-variant-has-quantity="true"]');
    if (firstAvailable instanceof HTMLInputElement && !this.#isUpdating) {
      firstAvailable.checked = true;
      this.updateProductData(firstAvailable);
      return;
    }

    const firstRadio = this.querySelector('input[type="radio"]');
    if (firstRadio instanceof HTMLInputElement) {
      firstRadio.checked = true;
    }
  }

  /** @param {Event} event */
  handleVariantChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (this.#isUpdating) return;

    this.updateProductData(event.target);
  }

  /** @param {HTMLInputElement} input */
  async updateProductData(input) {
    if (this.#isUpdating) return;
    this.#isUpdating = true;

    const variantImage = input.dataset.variantImage;
    const variantPrice = input.dataset.variantPrice;
    const variantCompareAtPrice = input.dataset.variantCompareAtPrice;
    const variantTitle = input.dataset.variantTitle;
    const variantDescription = input.dataset.variantDescription;
    const variantProductId = input.dataset.variantProductId;
    const variantVariantId = input.dataset.variantVariantId;
    const variantUrl = input.dataset.variantUrl;
    const variantBgColor1 = input.dataset.variantBgColor1;
    const variantBgColor2 = input.dataset.variantBgColor2;
    const variantBgGradient = input.dataset.variantBgGradient;

    if (!variantImage || !variantPrice || !variantTitle) {
      this.#isUpdating = false;
      return;
    }

    const productContext = this.getProductContext();
    const skipRender = this.dataset.skipRender === 'true';
    const isOnProductPage = this.isOnProductPage();

    if (isOnProductPage && variantUrl) {
      try {
        if (!skipRender) {
          const productSection = this.findProductSection();
          if (productSection) {
            const sectionId = productSection.id.replace('shopify-section-', '');
            const sectionUrl = new URL(variantUrl, window.location.origin);
            if (variantVariantId) {
              sectionUrl.searchParams.set('variant', variantVariantId);
            }

            await sectionRenderer.renderSection(sectionId, {
              url: sectionUrl,
              cache: false,
            });

            if (variantVariantId && variantProductId) {
              requestAnimationFrame(() => {
                this.updateSwatchSelection(variantProductId, variantVariantId, variantTitle);
                this.updateVariantPickerSelection(variantProductId);
              });
            }
          }
        } else {
          if (variantVariantId && variantProductId) {
            this.updateAddToCartForm(variantProductId, variantVariantId);
            this.updateQuantitySelector(variantVariantId);
          }
        }

        if (input) {
          this.updateBottleBadge(input);
          this.updateProductBadge(input);
        }

        this.#isUpdating = false;
      } catch (error) {
        console.error('Error updating product page:', error);
        this.#isUpdating = false;
      } finally {
        if (skipRender) {
          delete this.dataset.skipRender;
        }
      }
    } else {
      this.updateProductImage(variantImage);
      this.updateProductTitle(variantTitle);
      this.updateProductPrice(variantPrice, variantCompareAtPrice || '', variantProductId || '');
      if (variantDescription) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = variantDescription;
        const decodedDescription = tempDiv.textContent || tempDiv.innerText || variantDescription;
        this.updateProductDescription(decodedDescription);
      }
      this.updateProductBackground(variantBgColor1 || '', variantBgColor2 || '', variantBgGradient || '');

      if (variantVariantId && variantProductId) {
        this.updateAddToCartForm(variantProductId, variantVariantId);
        this.updateQuantitySelector(variantVariantId);
      }

      if (input) {
        this.updateBottleBadge(input);
        this.updateProductBadge(input);
        this.updateQuickAddButton(input);
      }
      this.#isUpdating = false;
    }

    if (isOnProductPage && variantUrl) {
      const url = new URL(window.location.href);
      const newUrl = new URL(variantUrl, window.location.origin);
      if (variantVariantId) {
        newUrl.searchParams.set('variant', variantVariantId);
      }
      if (url.pathname !== newUrl.pathname || url.search !== newUrl.search) {
        window.history.replaceState({}, '', newUrl.toString());
      }
    }

    this.dispatchEvent(
      new CustomEvent('metafield-variant-change', {
        bubbles: true,
        detail: {
          productId: variantProductId || '',
          image: variantImage,
          price: variantPrice,
          title: variantTitle,
          url: variantUrl || '',
        },
      })
    );
  }

  /**
   * Gets the variant ID from the URL query parameter
   * @returns {string | null}
   */
  getVariantIdFromUrl() {
    const url = new URL(window.location.href);
    return url.searchParams.get('variant');
  }

  /**
   * Checks if we're on a product page
   * @returns {boolean}
   */
  isOnProductPage() {
    return !!(
      document.querySelector('media-gallery') ||
      document.querySelector('[data-testid="product-information-media"]') ||
      document.querySelector('.product-information__grid')
    );
  }

  /**
   * Finds the product information section element
   * @returns {HTMLElement | null}
   */
  findProductSection() {
    const productInfoElement = document.querySelector(
      '.product-information, [data-testid="product-information-media"], .product-information__grid'
    );
    if (productInfoElement) {
      const section = productInfoElement.closest('.shopify-section');
      if (section instanceof HTMLElement && section.id) {
        return section;
      }
    }

    const mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery) {
      const section = mediaGallery.closest('.shopify-section');
      if (section instanceof HTMLElement && section.id) {
        return section;
      }
    }

    return null;
  }

  /** @param {string} color1 @param {string} color2 @param {string} gradient */
  updateProductBackground(color1, color2, gradient) {
    const productContext = this.getProductContext();
    if (!productContext) return;

    const productCard = productContext.querySelector('product-card') || productContext.closest('product-card');
    if (productCard instanceof HTMLElement) {
      if (gradient && gradient.trim() !== '') {
        if (color1) {
          productCard.style.setProperty('--product-bg-color', color1);
          productCard.dataset.color1 = color1;
        }
        if (gradient) {
          productCard.style.setProperty('--product-background-gradient', gradient);
        }
        if (color2) {
          productCard.dataset.color2 = color2;
        }
      } else {
        productCard.style.removeProperty('--product-bg-color');
        productCard.style.removeProperty('--product-background-gradient');
        delete productCard.dataset.color1;
        delete productCard.dataset.color2;
      }
    }

    const mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery) {
      const productMediaContainers = mediaGallery.querySelectorAll('.product-media');
      productMediaContainers.forEach((container) => {
        if (!(container instanceof HTMLElement)) return;

        if (gradient && gradient.trim() !== '') {
          if (color1) {
            container.style.setProperty('--product-bg-color', color1);
            container.setAttribute('data-color1', color1);
          }
          if (gradient) {
            container.style.setProperty('--product-background-gradient', gradient);
          }
          if (color2) {
            container.setAttribute('data-color2', color2);
          }
          const existingStyle = container.getAttribute('style') || '';
          const hasBgColor = existingStyle.includes('--product-bg-color');
          const hasGradient = existingStyle.includes('--product-background-gradient');
          if (!hasBgColor || !hasGradient) {
            let newStyle = existingStyle;
            if (color1 && !hasBgColor) {
              newStyle += ` --product-bg-color: ${color1};`;
            }
            if (gradient && !hasGradient) {
              newStyle += ` --product-background-gradient: ${gradient};`;
            }
            container.setAttribute('style', newStyle.trim());
          }
        } else {
          container.style.removeProperty('--product-bg-color');
          container.style.removeProperty('--product-background-gradient');
          container.removeAttribute('data-color1');
          container.removeAttribute('data-color2');
        }
      });
    }
  }

  /** @param {string} variantProductId @param {string} variantVariantId */
  updateAddToCartForm(variantProductId, variantVariantId) {
    if (!variantProductId || !variantVariantId) return;

    const productContext = this.getProductContext();
    const productForms = [];

    if (productContext) {
      const contextForms = productContext.querySelectorAll('product-form-component');
      contextForms.forEach((form) => productForms.push(form));
    }

    if (productForms.length === 0) {
      const formById = document.querySelector(`product-form-component[data-product-id="${variantProductId}"]`);
      if (formById) productForms.push(formById);
    }

    if (productForms.length === 0) {
      const anyForm = document.querySelector('product-form-component');
      if (anyForm) productForms.push(anyForm);
    }

    if (productForms.length === 0) return;

    const variantInput = this.querySelector(`input[data-variant-product-id="${variantProductId}"]`);
    const variantUrl = variantInput instanceof HTMLInputElement ? variantInput.dataset.variantUrl : undefined;

    productForms.forEach((productForm) => {
      const variantIdInput = productForm.querySelector('input[ref="variantId"], input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement) {
        variantIdInput.value = variantVariantId;
        variantIdInput.dispatchEvent(new Event('input', { bubbles: true }));
        variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const productIdInput = productForm.querySelector('input[name="product-id"]');
      if (productIdInput instanceof HTMLInputElement) {
        productIdInput.value = variantProductId;
      }

      if (productForm instanceof HTMLElement) {
        productForm.setAttribute('data-product-id', variantProductId);
        if (variantUrl) {
          productForm.setAttribute('data-product-url', variantUrl);
        }
      }

      productForm.dispatchEvent(
        new CustomEvent('variant-updated', {
          detail: {
            productId: variantProductId,
            variantId: variantVariantId,
          },
          bubbles: true,
        })
      );

      const quantitySelector = productForm.querySelector('quantity-selector-component');
      if (quantitySelector instanceof HTMLElement) {
        quantitySelector.setAttribute('data-variant-id', variantVariantId);
        const quantityInput = quantitySelector.querySelector('input[type="number"]');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.setAttribute('data-variant-id', variantVariantId);
          quantitySelector.dispatchEvent(
            new CustomEvent('variant-changed', {
              detail: { variantId: variantVariantId },
              bubbles: true,
            })
          );
        }
      }
    });
  }

  getProductContext() {
    const productId = this.dataset.productId;

    let productCard = this.closest('product-card');
    if (productCard instanceof HTMLElement) return productCard;

    const productCardContent = this.closest('.product-card__content');
    if (productCardContent instanceof HTMLElement) return productCardContent;

    productCard = this.closest('[data-product-id]');
    if (productCard instanceof HTMLElement) return productCard;

    if (productId) {
      const cardById = document.querySelector(`[data-product-id="${productId}"]`);
      if (cardById instanceof HTMLElement) return cardById;
    }

    const productInfoElement = document.querySelector(
      '.product-information, [data-testid="product-information-media"], .product-information__grid'
    );
    if (productInfoElement) {
      const productInfo =
        productInfoElement.closest('.shopify-section') ||
        productInfoElement.closest('.product-information__grid') ||
        productInfoElement;
      if (productInfo instanceof HTMLElement) return productInfo;
    }

    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement) return section;

    return document.body;
  }

  /** @param {string} imageUrl */
  updateProductImage(imageUrl) {
    const mediaGallery = document.querySelector('media-gallery');

    if (mediaGallery) {
      const widths = [375, 550, 750, 1100, 1500, 1780, 2000, 3000];
      const baseUrl = imageUrl.split('?')[0];
      const srcset = widths.map((w) => `${baseUrl}?width=${w} ${w}w`).join(', ');

      const firstSlide = mediaGallery.querySelector('slideshow-slide:first-child');
      if (firstSlide) {
        const firstSlideImages = firstSlide.querySelectorAll('.product-media img, img');
        firstSlideImages.forEach((img) => {
          if (img instanceof HTMLImageElement) {
            img.src = imageUrl;
            img.srcset = srcset;
            img.setAttribute('data_max_resolution', `${baseUrl}?width=3840`);
          }
        });
      }

      const mainMediaContainer = mediaGallery.querySelector(
        '[data-testid="media-gallery-grid"] .product-media-container:first-child, .product-media-container:first-child'
      );

      if (mainMediaContainer) {
        const mediaElements = mainMediaContainer.querySelectorAll('img, source');
        mediaElements.forEach((element) => {
          if (element instanceof HTMLImageElement) {
            element.src = imageUrl;
            element.srcset = srcset;
            element.setAttribute('data_max_resolution', `${baseUrl}?width=3840`);
            element.loading = 'lazy';
          } else if (element instanceof HTMLSourceElement) {
            element.srcset = srcset;
          }
        });
      }

      const firstThumbnail = mediaGallery.querySelector(
        '[data-testid="media-gallery-grid"] .product-media-container:first-child img'
      );
      if (firstThumbnail instanceof HTMLImageElement) {
        firstThumbnail.src = imageUrl;
        firstThumbnail.srcset = srcset;
        firstThumbnail.setAttribute('data_max_resolution', `${baseUrl}?width=3840`);
      }

      const zoomDialog = mediaGallery.querySelector('zoom-dialog');
      if (zoomDialog) {
        const firstZoomImage = zoomDialog.querySelector('.product-media img, img');
        if (firstZoomImage instanceof HTMLImageElement) {
          firstZoomImage.src = imageUrl;
          firstZoomImage.srcset = srcset;
          firstZoomImage.setAttribute('data_max_resolution', `${baseUrl}?width=3840`);
        }
      }

      return;
    }

    const productContext = this.getProductContext();
    if (productContext) {
      const cardGallerySelectors = [
        'slideshow img:first-of-type',
        'slideshow .slideshow-slide img',
        'slideshow img',
        '.card-gallery img',
        '.product-card-gallery img',
        '._product-card-gallery img',
        '[data-product-image] img',
        'a[ref="productCardLink"] ~ * img',
        'img[src*="products"]',
      ];

      for (const selector of cardGallerySelectors) {
        const images = productContext.querySelectorAll(selector);
        if (images.length > 0) {
          const mainImage = images[0];
          if (mainImage instanceof HTMLImageElement) {
            const widths = [375, 550, 750, 1100, 1500, 1780, 2000, 3000];
            const srcset = widths.map((w) => `${imageUrl.split('?')[0]}?width=${w} ${w}w`).join(', ');
            mainImage.src = imageUrl;
            mainImage.srcset = srcset;
            return;
          }
        }
      }
    }

    const fallbackSelectors = ['.product-media img', 'product-media img', '[data-product-media] img'];
    const searchScope = productContext || document;

    for (const selector of fallbackSelectors) {
      const images = searchScope.querySelectorAll(selector);
      if (images.length > 0) {
        const widths = [375, 550, 750, 1100, 1500, 1780, 2000, 3000];
        const srcset = widths.map((w) => `${imageUrl.split('?')[0]}?width=${w} ${w}w`).join(', ');
        images.forEach((img) => {
          if (img instanceof HTMLImageElement) {
            img.src = imageUrl;
            img.srcset = srcset;
          }
        });
        break;
      }
    }
  }

  /** @param {string} title */
  updateProductTitle(title) {
    const titleParts = title.split(' - ');
    const displayTitle = titleParts.length > 1 && titleParts[0] ? titleParts[0].trim() : title;
    const fullTitle = title;

    const productContext = this.getProductContext();
    if (!productContext) return;

    const isOnProductPage =
      productContext &&
      (document.querySelector('media-gallery') ||
        document.querySelector('[data-testid="product-information-media"]') ||
        document.querySelector('.product-information__grid') ||
        this.closest('.shopify-section')?.querySelector('media-gallery'));

    const searchScope = isOnProductPage ? document : productContext;

    const titleLink = searchScope.querySelector('.view-product-title a');
    if (titleLink instanceof HTMLAnchorElement) {
      titleLink.textContent = displayTitle;
    }

    const allH1s = searchScope.querySelectorAll('.product-details h1,  h1, .view-product-title ~ * h1');
    for (const h1 of allH1s) {
      if (isOnProductPage || productContext.contains(h1)) {
        if (!h1.closest('.text-block')) {
          h1.textContent = fullTitle;
          break;
        }
      }
    }

    const titleSelectors = ['product-title', '[ref="productTitleLink"]', 'h1.product-title', '.product-title'];

    for (const selector of titleSelectors) {
      const titleElements = searchScope.querySelectorAll(selector);
      if (titleElements.length > 0) {
        titleElements.forEach((element) => {
          if (!isOnProductPage && !productContext.contains(element)) {
            return;
          }

          if (element instanceof HTMLAnchorElement) {
            const textElement = element.querySelector('p, span, h1, h2, h3, h4, h5, h6') || element;
            if (textElement) {
              textElement.textContent = displayTitle;
            }
          } else if (element.tagName === 'PRODUCT-TITLE') {
            const link = element.querySelector('a[ref="productTitleLink"]');
            if (link) {
              const textElement = link.querySelector('p, span, h1, h2, h3, h4, h5, h6') || link;
              if (textElement) {
                textElement.textContent = displayTitle;
              }
            } else {
              const textElement = element.querySelector('p, span, h1, h2, h3, h4, h5, h6, a') || element;
              if (textElement) {
                textElement.textContent = displayTitle;
              }
            }
          } else {
            const textElement = element.querySelector('p, span, h1, h2, h3, h4, h5, h6, a');
            if (textElement && textElement !== element) {
              textElement.textContent = displayTitle;
            } else {
              element.textContent = displayTitle;
            }
          }
        });
        break;
      }
    }
  }

  /** @param {string} price @param {string} compareAtPrice @param {string} productId */
  updateProductPrice(price, compareAtPrice, productId) {
    const productContext = this.getProductContext();
    const priceElement = productContext
      ? productContext.querySelector('product-price')
      : document.querySelector('product-price');

    if (priceElement) {
      const priceContainer = priceElement.querySelector('[ref="priceContainer"]');
      if (priceContainer) {
        const hasCompareAtPrice =
          compareAtPrice && compareAtPrice.trim() !== '' && compareAtPrice !== '0' && compareAtPrice !== price;

        if (hasCompareAtPrice) {
          const existingCompareAt = priceContainer.querySelector('.compare-at-price');
          const existingPrice = priceContainer.querySelector('.price:not(.compare-at-price)');

          if (existingPrice) {
            existingPrice.textContent = price;
          } else {
            priceContainer.innerHTML = `<span class="price">${price}</span>`;
          }

          if (existingCompareAt) {
            existingCompareAt.textContent = compareAtPrice;
          } else {
            const compareAtSpan = document.createElement('span');
            compareAtSpan.className = 'compare-at-price';
            compareAtSpan.textContent = compareAtPrice;
            priceContainer.insertBefore(compareAtSpan, priceContainer.firstChild);
          }
        } else {
          const existingCompareAt = priceContainer.querySelector('.compare-at-price');
          if (existingCompareAt) {
            existingCompareAt.remove();
          }

          const priceSpan = priceContainer.querySelector('.price:not(.compare-at-price)');
          if (priceSpan) {
            priceSpan.textContent = price;
          } else {
            priceContainer.innerHTML = `<span class="price">${price}</span>`;
          }
        }
        return;
      }
    }

    const priceSelectors = ['[ref="priceContainer"]', '[data-product-price]', '.product-price'];
    const searchScope = productContext || document;

    for (const selector of priceSelectors) {
      const priceElements = searchScope.querySelectorAll(selector);
      if (priceElements.length > 0) {
        priceElements.forEach((element) => {
          const priceContainer = element.querySelector('[ref="priceContainer"]') || element;
          if (priceContainer) {
            const hasCompareAtPrice =
              compareAtPrice && compareAtPrice.trim() !== '' && compareAtPrice !== '0' && compareAtPrice !== price;

            if (hasCompareAtPrice) {
              const existingCompareAt = priceContainer.querySelector('.compare-at-price');
              const existingPrice = priceContainer.querySelector('.price:not(.compare-at-price)');

              if (existingPrice) {
                existingPrice.textContent = price;
              } else {
                priceContainer.innerHTML = `<span class="price">${price}</span>`;
              }

              if (existingCompareAt) {
                existingCompareAt.textContent = compareAtPrice;
              } else {
                const compareAtSpan = document.createElement('span');
                compareAtSpan.className = 'compare-at-price';
                compareAtSpan.textContent = compareAtPrice;
                priceContainer.insertBefore(compareAtSpan, priceContainer.firstChild);
              }
            } else {
              const existingCompareAt = priceContainer.querySelector('.compare-at-price');
              if (existingCompareAt) {
                existingCompareAt.remove();
              }

              const priceSpan = priceContainer.querySelector('.price:not(.compare-at-price)');
              if (priceSpan) {
                priceSpan.textContent = price;
              } else {
                priceContainer.innerHTML = `<span class="price">${price}</span>`;
              }
            }
          }
        });
        break;
      }
    }
  }

  /** @param {string} description */
  updateProductDescription(description) {
    const productContext = this.getProductContext();
    if (!productContext) return;

    const isOnProductPage =
      productContext &&
      (document.querySelector('media-gallery') ||
        document.querySelector('[data-testid="product-information-media"]') ||
        document.querySelector('.product-information__grid') ||
        this.closest('.shopify-section')?.querySelector('media-gallery'));

    const searchScope = isOnProductPage ? document : productContext;

    const allTextBlocks = searchScope.querySelectorAll('.text-block');

    for (const textBlock of allTextBlocks) {
      if (!isOnProductPage && !productContext.contains(textBlock)) {
        continue;
      }

      if (
        textBlock.querySelector('h1, product-title, [ref="productTitleLink"]') ||
        textBlock.closest('product-price') ||
        textBlock.querySelector('product-price')
      ) {
        continue;
      }

      const allElements = textBlock.querySelectorAll('p, div');
      for (const element of allElements) {
        const text = element.textContent.trim();
        const normalizedText = text.replace(/\s+/g, ' ').toLowerCase();
        if (
          normalizedText &&
          ((normalizedText.includes('x') && (normalizedText.match(/\d+x\s*\d+/) || normalizedText.includes(' - '))) ||
            (normalizedText.includes(' - ') && normalizedText.includes('x')))
        ) {
          if (element.tagName === 'P') {
            element.textContent = description;
          } else {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = description;
            element.innerHTML = tempDiv.innerHTML || description;
          }
          return;
        }
      }
    }

    const priceElement = searchScope.querySelector('product-price');
    if (priceElement) {
      if (!isOnProductPage && !productContext.contains(priceElement)) {
        return;
      }

      const textBlocksArray = Array.from(searchScope.querySelectorAll('.text-block'));
      const priceIndex = textBlocksArray.findIndex(
        (block) => block.contains(priceElement) || block === priceElement || block.querySelector('product-price')
      );

      if (priceIndex >= 0) {
        for (let i = priceIndex + 1; i < textBlocksArray.length; i++) {
          const textBlock = textBlocksArray[i];
          if (!textBlock) continue;

          if (!isOnProductPage && !productContext.contains(textBlock)) {
            continue;
          }

          if (textBlock.querySelector('h1, product-title, product-price') || textBlock.closest('product-price')) {
            continue;
          }

          const allElements = textBlock.querySelectorAll('p, div');
          if (allElements.length > 0) {
            for (const element of allElements) {
              const elementText = element.textContent.trim();
              if (elementText) {
                if (element.tagName === 'P') {
                  element.textContent = description;
                } else {
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = description;
                  element.innerHTML = tempDiv.innerHTML || description;
                }
                return;
              }
            }
            if (allElements[0]) {
              if (allElements[0].tagName === 'P') {
                allElements[0].textContent = description;
              } else {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = description;
                allElements[0].innerHTML = tempDiv.innerHTML || description;
              }
              return;
            }
          }
        }
      }
    }
  }

  /**
   * Updates the swatch selection after section render
   * Uses requestAnimationFrame to ensure it runs after connectedCallback
   * @param {string} variantProductId
   * @param {string} variantVariantId
   * @param {string} variantTitle
   */
  updateSwatchSelection(variantProductId, variantVariantId, variantTitle) {
    const updateSwatch = () => {
      const productSwatches = document.querySelector('product-swatches');
      if (!productSwatches) {
        requestAnimationFrame(updateSwatch);
        return;
      }

      const swatches = productSwatches.querySelectorAll('.product-swatches__swatch');
      if (swatches.length === 0) {
        requestAnimationFrame(updateSwatch);
        return;
      }

      let flavourName = '';
      if (variantTitle) {
        const titleParts = variantTitle.split(' - ');
        flavourName = titleParts.length > 1 && titleParts[0] ? titleParts[0].trim() : variantTitle;
      }

      let swatch = null;
      if (variantProductId) {
        swatch = productSwatches.querySelector(`[data-product-id="${variantProductId}"]`);
      }

      if (!swatch && variantVariantId) {
        swatch = productSwatches.querySelector(`[data-variant-id="${variantVariantId}"]`);
      }

      if (!swatch && flavourName) {
        for (const s of swatches) {
          if (s instanceof HTMLButtonElement) {
            const swatchFlavourName = (s.dataset.flavourName || '').toLowerCase();
            if (swatchFlavourName === flavourName.toLowerCase()) {
              swatch = s;
              break;
            }
          }
        }
      }

      if (swatch instanceof HTMLButtonElement) {
        const swatchesElement = /** @type {any} */ (productSwatches);
        if (typeof swatchesElement.selectSwatch === 'function') {
          swatchesElement.selectSwatch(swatch);
        } else {
          productSwatches.querySelectorAll('.product-swatches__swatch').forEach((s) => {
            s.classList.remove('product-swatches__swatch--selected');
          });
          swatch.classList.add('product-swatches__swatch--selected');
          const swatchFlavourName = swatch.dataset.flavourName || flavourName;
          const selectedFlavourElement = productSwatches.querySelector('.product-swatches__selected-flavour');
          if (selectedFlavourElement && swatchFlavourName) {
            selectedFlavourElement.textContent = swatchFlavourName;
          }
        }
      } else if (flavourName) {
        const selectedFlavourElement = productSwatches.querySelector('.product-swatches__selected-flavour');
        if (selectedFlavourElement) {
          selectedFlavourElement.textContent = flavourName;
        }
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(updateSwatch);
    });
  }

  /**
   * Updates the variant picker selection after section render
   * @param {string} variantProductId
   */
  updateVariantPickerSelection(variantProductId) {
    requestAnimationFrame(() => {
      const variantPicker = document.querySelector('product-metafield-variant-picker');
      if (!variantPicker) return;

      const targetRadio = variantPicker.querySelector(`input[data-variant-product-id="${variantProductId}"]`);
      if (targetRadio instanceof HTMLInputElement) {
        const allRadios = variantPicker.querySelectorAll('input[type="radio"]');
        allRadios.forEach((radio) => {
          if (radio instanceof HTMLInputElement) {
            radio.checked = false;
          }
        });
        targetRadio.checked = true;
      }
    });
  }

  /** @param {string} variantId */
  updateQuantitySelector(variantId) {
    const productContext = this.getProductContext();
    if (!productContext) return;

    const quantitySelectors = productContext.querySelectorAll(
      'quantity-selector-component, cart-quantity-selector-component'
    );
    quantitySelectors.forEach((selector) => {
      if (selector instanceof HTMLElement) {
        selector.setAttribute('data-variant-id', variantId);
        const quantityInput = selector.querySelector('input[type="number"]');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.setAttribute('data-variant-id', variantId);
          selector.dispatchEvent(new CustomEvent('variant-changed', { detail: { variantId } }));
        }
      }
    });
  }

  /** @param {HTMLInputElement} input */
  updateBottleBadge(input) {
    const productContext = this.getProductContext();
    if (!productContext) return;

    const bottleBadgeContainer = productContext.querySelector('.product-custom-badge-bottle-count');
    if (!bottleBadgeContainer) return;

    bottleBadgeContainer.innerHTML = '';

    const bottlesCount = input.dataset.variantBottlesCount;
    if (!bottlesCount || bottlesCount.trim() === '') return;

    const badge = document.createElement('div');
    badge.className = 'product-custom-badge bottle-badge';

    const count = bottlesCount.trim();
    const isSingular = count === '1';
    const bottleText = isSingular ? 'bottle' : 'bottles';

    badge.innerHTML = `<span class="bottle-badge-count">${count} </span><span class="bottle-badge-text">${bottleText}</span>`;
    bottleBadgeContainer.appendChild(badge);
  }

  /** @param {HTMLInputElement} input */
  updateProductBadge(input) {
    const productContext = this.getProductContext();
    if (!productContext) return;

    const badgeContainer = productContext.querySelector('.product-badges');
    if (!badgeContainer || !(badgeContainer instanceof HTMLElement)) return;

    const variantAvailable = input.dataset.variantAvailable === 'true';
    const variantInventoryQuantity = parseInt(input.dataset.variantInventoryQuantity || '0', 10);
    const variantInventoryPolicy = input.dataset.variantInventoryPolicy || '';
    const variantInventoryManagement = input.dataset.variantInventoryManagement || '';

    let variantHasQuantity = false;
    if (variantInventoryManagement === 'shopify') {
      if (variantInventoryQuantity > 0 || variantInventoryPolicy === 'continue') {
        variantHasQuantity = true;
      }
    } else {
      if (variantAvailable) {
        variantHasQuantity = true;
      }
    }

    let badgeElement = badgeContainer.querySelector('.product-badges__badge');
    const priceElement = productContext.querySelector('product-price, [ref="priceContainer"]');
    let isOnSale = false;
    if (priceElement) {
      const compareAtPrice = priceElement.querySelector('.price--compare-at, .price-compare');
      const regularPrice = priceElement.querySelector('.price:not(.price--compare-at):not(.price-compare)');
      if (compareAtPrice && regularPrice) {
        const compareAtText = compareAtPrice.textContent.replace(/[^0-9.,]/g, '').replace(',', '.');
        const regularText = regularPrice.textContent.replace(/[^0-9.,]/g, '').replace(',', '.');
        const compareAtValue = parseFloat(compareAtText);
        const regularValue = parseFloat(regularText);
        if (!isNaN(compareAtValue) && !isNaN(regularValue)) {
          isOnSale = compareAtValue > regularValue && variantHasQuantity;
        }
      }
    }

    if (!variantHasQuantity) {
      if (!badgeElement) {
        badgeElement = document.createElement('div');
        badgeElement.className = 'product-badges__badge product-badges__badge--rectangle';
        badgeContainer.appendChild(badgeElement);
      }
      badgeElement.className = 'product-badges__badge product-badges__badge--rectangle color-scheme-3';
      badgeElement.textContent = 'Sold out';
      badgeContainer.style.display = '';
    } else if (isOnSale) {
      if (!badgeElement) {
        badgeElement = document.createElement('div');
        badgeElement.className = 'product-badges__badge product-badges__badge--rectangle';
        badgeContainer.appendChild(badgeElement);
      }
      const saleColorScheme = 'scheme-2';
      badgeElement.className = `product-badges__badge product-badges__badge--rectangle color-${saleColorScheme}`;
      badgeElement.textContent = 'Sale';
      badgeContainer.style.display = '';
    } else {
      badgeContainer.style.display = 'none';
    }
  }

  /** @param {HTMLInputElement} input */
  updateQuickAddButton(input) {
    const productContext = this.getProductContext();
    if (!productContext) return;

    const productCardContent = productContext.closest('.product-card__content') || productContext.querySelector('.product-card__content') || productContext;
    const quickAddComponent = productContext.querySelector('quick-add-component');
    if (!(quickAddComponent instanceof HTMLElement)) return;

    const variantHasQuantity = input.dataset.variantHasQuantity === 'true';

    if (variantHasQuantity) {
      if (productCardContent instanceof HTMLElement) {
        productCardContent.style.setProperty('--quick-add-display', 'flex');
      }
      quickAddComponent.style.removeProperty('display');

      const addToCartButton = quickAddComponent.querySelector('button[type="submit"]');
      if (addToCartButton instanceof HTMLButtonElement) {
        addToCartButton.disabled = false;

        const addToCartTextSpan = addToCartButton.querySelector('.add-to-cart-text');
        if (addToCartTextSpan) {
          let iconSpan = addToCartTextSpan.querySelector('.add-to-cart-icon');
          if (!iconSpan) {
            iconSpan = document.createElement('span');
            iconSpan.className = 'svg-wrapper add-to-cart-icon';
            iconSpan.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"></path><path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width)" d="M14.608 12.563v5m2.5-2.5h-5"></path></svg>`;
            addToCartTextSpan.insertBefore(iconSpan, addToCartTextSpan.firstChild);
          }

          const buttonText = addToCartTextSpan.querySelector('.add-to-cart-text__content');
          if (buttonText) {
            buttonText.textContent = 'Add';
          }
        }
      }
      const variantIdInput = quickAddComponent.querySelector('input[name="id"], input[ref="variantId"]');
      if (variantIdInput instanceof HTMLInputElement) {
        variantIdInput.disabled = false;
      }
    } else {
      if (productCardContent instanceof HTMLElement) {
        productCardContent.style.setProperty('--quick-add-display', 'none');
      }
      quickAddComponent.style.removeProperty('display');
    }

    // Blur active element to reset hover state
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }
}

if (!customElements.get('product-metafield-variant-picker')) {
  customElements.define('product-metafield-variant-picker', ProductMetafieldVariantPicker);
}
